import { _ } from "i18n";
import { CommonModal } from "../../../../../../ui-kit-v2/src/modal/common-modal";
import "./playerRatingModal.css";
import { useState } from "react";
import {
	BadgesPutRequest,
	type GPGamePlayerResponse,
	PlayerBadgesModalData,
	PlayerBadgesModalMode,
	RatingBadgeResponse,
} from "../../../../gamePage/types";
import { PlayerBadgesTab } from "./PlayerBadgesTab";
import { PlayerModalPlayerSelect } from "./PlayerModalPlayerSelect";

interface PlayerBadgesModalProps {
	modalData: PlayerBadgesModalData;
	badges: RatingBadgeResponse[];
	close: () => void;
	onSubmit?: (id: number, badges: BadgesPutRequest) => void;
	showPlayersSelect?: boolean;
	players?: GPGamePlayerResponse[];
	maxBadgeCount?: number;
	UNSTABLE_portalContainer?: Element;
}

const initModalData: PlayerBadgesModalData = {
	id: 0,
	show: false,
	name: null,
	badgeIds: [],
	mode: PlayerBadgesModalMode.Save,
};

const areBadgeIdsEqual = (left: number[], right: number[]): boolean => {
	if (left.length !== right.length) {
		return false;
	}

	const sortedLeft = [...left].sort((a, b) => a - b);
	const sortedRight = [...right].sort((a, b) => a - b);

	return sortedLeft.every((badgeId, index) => badgeId === sortedRight[index]);
};

export function PlayerBadgesModal({
	modalData,
	close,
	badges,
	onSubmit,
	showPlayersSelect,
	players,
	maxBadgeCount,
	UNSTABLE_portalContainer,
}: PlayerBadgesModalProps) {
	const [modalState, setModalState] = useState<PlayerBadgesModalData>(modalData);
	const [initBadgeIds, setInitBadgeIds] = useState<number[]>(modalData.badgeIds);

	const isAttachMode = modalData.mode === PlayerBadgesModalMode.Attach;
	const canShowPlayersSelect = showPlayersSelect === true && isAttachMode === false;
	const isSaveDisabled = modalState.id === 0 || areBadgeIdsEqual(modalState.badgeIds, initBadgeIds);

	const primaryButtonText = isAttachMode ? _("gamePage.playerBadges.modal.attachButton") : _("gamePage.playerRating.modal.primaryButton");
	const badgesTooltipText = isAttachMode
		? _("gamePage.playerBadges.modal.attachTooltip")
		: _("gamePage.playerRating.modal.visibility.badgesTooltip");

	const handlePlayerChange = (playerId: number, playerName: string) => {
		const selectedPlayer = players?.find((player) => player.id === playerId);
		const playerBadgeIds = selectedPlayer?.coachRating?.badgeIds ?? [];

		setInitBadgeIds(playerBadgeIds);
		setModalState({
			...modalState,
			id: playerId,
			name: selectedPlayer?.name ?? playerName,
			badgeIds: playerBadgeIds,
		});
	};

	return (
		<CommonModal
			show={modalData.show}
			title={_("gamePage.playerBadges.modal.title")}
			size="lg"
			onClose={close}
			UNSTABLE_portalContainer={UNSTABLE_portalContainer}
			primaryButton={{
				text: primaryButtonText,
				disabled: isSaveDisabled,
				onClick: () => {
					if (onSubmit) {
						onSubmit(modalState.id, {
							badgeIds: modalState.badgeIds,
						});

						if (canShowPlayersSelect) {
							setModalState(initModalData);
						}
						close();
					}
				},
			}}
			secondaryButton={{
				text: _("gamePage.playerRating.modal.secondaryButton"),
				onClick: () => {
					if (canShowPlayersSelect) {
						setModalState(initModalData);
					}
					close();
				},
			}}
		>
			<div className="playerRatingModal-container">
				{canShowPlayersSelect && players != null && (
					<PlayerModalPlayerSelect
						label={_("gamePage.playerBadges.modal.selectPlayer")}
						placeholder={_("gamePage.playerRatingBadges.modal.selectPlayerPlaceholder")}
						players={players}
						onPlayerChange={handlePlayerChange}
					/>
				)}
				<PlayerBadgesTab
					initBadgeIds={initBadgeIds}
					badges={badges}
					modalState={modalState}
					setModalState={setModalState}
					maxBadgeCount={maxBadgeCount}
					lockInitBadges={isAttachMode === false}
				/>
				<div className="playerRatingModal-badgesTooltip">{badgesTooltipText}</div>
			</div>
		</CommonModal>
	);
}
